import React, {FC, useEffect, useState} from "react"
import Flex from "@irati/ui/src/Flex"
import {H1, H5, H6, Label} from "@irati/ui/src/Typography"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {useTheme} from "@irati/ui/src/global/useTheme"
import PropTypes from "prop-types"
import mySavingsTracking from "@irati-tracking/my-savings/mySavings"
import {SavingsCalculatorDealBrand} from "@irati/types"
import authentication from "@irati/auth"
import {ProTip} from "./styles"

interface Props {
  brandList: any
  selectedUuid?: string
  currency: string
  selectedAmount: string
}

const WEEKS_IN_YEAR = 52

const RightColumnContent: FC<Props> = ({brandList, selectedUuid, currency, selectedAmount}, {apps, app}) => {
  const theme = useTheme()
  const [selectedBrand, setSelectedBrand] = useState<SavingsCalculatorDealBrand>({})

  const hasFP = authentication.apps("wallet.treatTokens")

  const hasFPMessageList = [
    "platform.mySavingsCalculator.proTips.hasFP.messageOne",
    "platform.mySavingsCalculator.proTips.hasFP.messageTwo",
    "platform.mySavingsCalculator.proTips.hasFP.messageThree",
    "platform.mySavingsCalculator.proTips.hasFP.messageFour"
  ]
  const noFPMessageList = ["platform.mySavingsCalculator.proTips.noFP.messageOne", "platform.mySavingsCalculator.proTips.noFP.messageTwo"]

  const proTipMessage = hasFP
    ? hasFPMessageList[Math.floor(Math.random() * hasFPMessageList.length)]
    : noFPMessageList[Math.floor(Math.random() * noFPMessageList.length)]

  useEffect(() => {
    const brand = brandList?.find((b) => b.uuid === selectedUuid)
    setSelectedBrand(brand)
  }, [selectedUuid, brandList])

  const handleClick = () => {
    mySavingsTracking.clickSavingsCalculatorBtn(app, {
      brand: selectedBrand?.name || "unselected",
      amount: selectedAmount
    })
  }

  const discountsArray = brandList?.map((item: SavingsCalculatorDealBrand) => item.discount)
  const minDiscount = Math.min(...(discountsArray ?? []))
  const maxDiscount = Math.max(...(discountsArray ?? []))
  const minSavingValue = parseFloat(selectedAmount) * WEEKS_IN_YEAR * (minDiscount / 100)
  const maxSavingValue = parseFloat(selectedAmount) * WEEKS_IN_YEAR * (maxDiscount / 100)
  const selectedSavingValue = parseFloat(selectedAmount) * WEEKS_IN_YEAR * (selectedBrand?.discount / 100)
  const weeksWorthOfSavings = Math.round(WEEKS_IN_YEAR * (selectedBrand?.discount / 100))

  return (
    <Flex direction="column" gap={2}>
      <Flex direction="column">
        <H1 color={theme.colors.greys.white}>
          {selectedUuid ? (
            <FormattedCurrency isCents={false} currency={currency} value={selectedSavingValue} maximumFractionDigits={0} />
          ) : minSavingValue === maxSavingValue ? (
            <FormattedCurrency isCents={false} currency={currency} value={maxSavingValue} maximumFractionDigits={0} />
          ) : (
            <>
              <FormattedCurrency isCents={false} currency={currency} value={minSavingValue} maximumFractionDigits={0} /> {" - "}
              <FormattedCurrency isCents={false} currency={currency} value={maxSavingValue} maximumFractionDigits={0} />
            </>
          )}
          <Label color={theme.colors.greys.white} marginLeft={theme.spacing.half}>
            <FormattedMessage id="platform.mySavingsCalculator.inSavings" />
          </Label>
        </H1>

        {selectedUuid ? (
          <H5 color={theme.colors.greys.white}>
            <FormattedMessage id="platform.mySavingsCalculator.description.numberOfWeeks" values={{count: weeksWorthOfSavings}} />
          </H5>
        ) : minSavingValue === maxSavingValue ? (
          <H5 color={theme.colors.greys.white}>
            <FormattedMessage
              id="platform.mySavingsCalculator.description.oneValue"
              values={{
                value: <FormattedCurrency isCents={false} currency={currency} value={maxSavingValue} maximumFractionDigits={0} />,
                brand: null
              }}
            />
          </H5>
        ) : (
          <H5 color={theme.colors.greys.white}>
            <FormattedMessage
              id="platform.mySavingsCalculator.description.twoValues"
              values={{
                min: <FormattedCurrency isCents={false} currency={currency} value={minSavingValue} maximumFractionDigits={0} />,
                max: <FormattedCurrency isCents={false} currency={currency} value={maxSavingValue} maximumFractionDigits={0} />
              }}
            />
          </H5>
        )}
      </Flex>

      <ProTip>
        <H6 color={theme.colors.greys.white}>
          <FormattedMessage id="platform.mySavingsCalculator.proTips.prefix" />
          <Label color={theme.colors.greys.white}>
            <FormattedMessage id={proTipMessage} />
          </Label>
        </H6>
      </ProTip>

      {!selectedUuid ? (
        <ButtonRouterLink
          primary
          inverted
          app={apps.PERKS}
          to="/categories/food-and-drink?tags=supermarkets&sort=popularity"
          onClick={handleClick}>
          <FormattedMessage id="platform.mySavingsCalculator.cta.title" />
        </ButtonRouterLink>
      ) : selectedBrand?.dealSlug ? (
        <ButtonRouterLink primary inverted app={apps.PERKS} to={`/info/${selectedBrand?.dealSlug}`} onClick={handleClick}>
          <FormattedMessage id="platform.mySavingsCalculator.cta.title" />
        </ButtonRouterLink>
      ) : (
        <ButtonRouterLink primary inverted app={apps.PERKS} to="/categories" onClick={handleClick}>
          <FormattedMessage id="platform.mySavingsCalculator.cta.title" />
        </ButtonRouterLink>
      )}
    </Flex>
  )
}

RightColumnContent.contextTypes = {
  app: PropTypes.string.isRequired,
  apps: PropTypes.object.isRequired
}

export default RightColumnContent
